import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { red, grey } from "@mui/material/colors";
import { createUser, createSuperAdminUser, getUsers } from "../../../api/userApi";
import { notification } from "../../../utils/Notifications";
import useLogout from '../../../hooks/auth/authHook.js';
import {
  Button,
  Container,
  Dialog,
  FormControl,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { TickCircle } from "iconsax-react";
import styles from "./deliveryFormDialogStyles.js";

DeliveryFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  zones: PropTypes.array,
};

export default function DeliveryFormDialog({ onClose, open, fetchUsers, zones = [] }) {
  const [logout] = useLogout();

  const [containsRightLenght, setContainsRightLenght] = useState(false);
  const [containsLetter, setContainsLetter] = useState(false);
  const [containsNumbers, setContainsNumbers] = useState(false);
  const [containsSpecialCharacters, setContainsSpecialCharacters] = useState(false);

  const [name, setName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [errors, setErrors] = useState({});
  const [selectedZone, setSelectedZone] = useState("");

  const handleNameChange = (event) => setName(event.target.value);
  const handleMiddleNameChange = (event) => setMiddleName(event.target.value);
  const handleLastNameChange = (event) => setLastName(event.target.value);
  const handlePhoneChange = (event) => {
    setPhoneNumber(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumber: !validatePhoneNumber(event.target.value)
        ? "El teléfono debe tener 10 números"
        : "",
    }));
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      email: !validateEmail(event.target.value)
        ? "Correo electrónico no válido"
        : "",
    }));
  };
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleZoneChange = (event) => setSelectedZone(event.target.value);

  const validatePhoneNumber = (phoneNumber) => {
    const regex = /^\d{10}$/;
    return regex.test(phoneNumber);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = () => {
    const letterRegex = /[A-Za-z]/;
    const numberRegex = /[0-9]/;
    const specialCharRegex = /[!@#$%^&*]/;

    const containsRightLenght = password.length >= 8;
    const containsLetter = letterRegex.test(password);
    const containsNumbers = numberRegex.test(password);
    const containsSpecialCharacters = specialCharRegex.test(password);

    setContainsRightLenght(containsRightLenght);
    setContainsLetter(containsLetter);
    setContainsNumbers(containsNumbers);
    setContainsSpecialCharacters(containsSpecialCharacters);

    return containsLetter && containsNumbers && containsSpecialCharacters && containsRightLenght;
  };

  const clearFields = () => {
    setName("");
    setMiddleName("");
    setLastName("");
    setPhoneNumber("");
    setEmail("");
    setPassword("");
    setErrors({});
    setSelectedZone("");
  };

  const handleCreateDeliveryClick = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    const user = {
      role: "delivery",
      name,
      middleName,
      lastName,
      phoneNumber,
      email,
      password,
    };

    if (isSuperAdmin) {
      user.available_zones = selectedZone ? [Number(selectedZone)] : [];
    }

    const createFunction = isSuperAdmin ? createSuperAdminUser : createUser;

    createFunction(user)
      .then((response) => {
        if (response.data.status_Code === 200 && response.data.status_Message === "exist email") {
          notification({ text: "El correo ya esta en uso", type: "error" });
          setIsLoading(false);
          return;
        }

        if (response.data.status_Code === 201) {
          notification({ text: "Repartidor creado", type: "success" });
          getUsers()
          .then((response) => {
            if (response.data.status_Code === 200) {
              fetchUsers();
              onClose();
            } else if (response.data.status_Code === 401) {
              notification({
                text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
                type: "error",
              });
              onClose();
              logout();
            } else {
              notification({
                text: "Error al obtener los usuarios",
                type: "error",
              });
              onClose();
            }
          })
          .catch(() => {
            notification({text: 'Error de conexión, revisa tu conexión a internet', type: 'error'});
            onClose();
          })
          .finally(() => {
            clearFields();
            setIsLoading(false);
          });
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al crear el repartidor",
            type: "error",
          });
        }
      })
      .catch(() => {
        setIsLoading(false);
        clearFields();
        notification({text: 'Error de conexión, revisa tu conexión a internet', type: 'error'});
      });
  };

  useEffect(() => {
    const validateForm = () => {
      const basicValid =
        name && middleName && lastName && phoneNumber && email && password;
      const zoneValid = isSuperAdmin ? selectedZone : true;
      const hasNoErrors =
        !errors.email && !errors.phoneNumber && validatePassword();

      setIsFormValid(basicValid && zoneValid && hasNoErrors);
    };

    validateForm();
  }, [
    name,
    middleName,
    lastName,
    phoneNumber,
    email,
    password,
    errors,
    selectedZone,
    isSuperAdmin,
  ]);

  useEffect(() => {
    if (!open) {
      clearFields();
    }
  }, [open]);

  const buttonStyles = {
    width: "120px",
    borderRadius: "0.5rem",
    height: "38px",
  };

  const activeButtonStyles = {
    ...buttonStyles,
    backgroundColor: red["A400"],
    color: "white",
    "&:hover": {
      backgroundColor: red["A700"],
    },
  };

  const inactiveButtonStyles = {
    ...buttonStyles,
    backgroundColor: grey[500],
    color: grey[300],
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      onClose={handleDialogClose}
      open={open}
      PaperProps={{ style: { borderRadius: "16px" } }}
    >
      <Container style={{ padding: 36 }}>
        <Typography
          component="div"
          variant="h5"
          style={{
            textAlign: "center",
            fontWeight: "bold",
            color: grey[800],
            marginBottom: 26,
          }}
        >
          Nuevo repartidor
        </Typography>
        <FormControl variant="standard">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Nombre"
                variant="standard"
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                style={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Apellido paterno"
                variant="standard"
                value={middleName}
                onChange={handleMiddleNameChange}
                error={!!errors.middleName}
                helperText={errors.middleName}
                style={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Apellido materno"
                variant="standard"
                value={lastName}
                onChange={handleLastNameChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                style={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Teléfono"
                variant="standard"
                value={phoneNumber}
                onChange={handlePhoneChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                style={{ width: "100%" }}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Correo electrónico"
                variant="standard"
                value={email}
                onChange={handleEmailChange}
                error={!!errors.email}
                helperText={errors.email}
                style={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={isSuperAdmin ? 4 : 6} md={isSuperAdmin ? 4 : 4}>
              <TextField
                id="standard-basic"
                label="* Contraseña"
                variant="standard"
                value={password}
                onChange={handlePasswordChange}
                error={!!errors.password}
                helperText={errors.password}
                style={{ width: "100%" }}
              />
            </Grid>
            {isSuperAdmin && (
              <Grid item xs={12} sm={4} md={4}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="delivery-zone-select-label">* Zona</InputLabel>
                  <Select
                    labelId="delivery-zone-select-label"
                    id="delivery-zone-select"
                    value={selectedZone}
                    onChange={handleZoneChange}
                  >
                    {zones.map((zone) => {
                      const zoneId =
                        typeof zone === "string"
                          ? zone
                          : zone.id_zone ??
                            zone.zone_id ??
                            zone.id ??
                            zone.zone_code ??
                            zone.name ??
                            zone.zone_name;
                      const zoneLabel =
                        typeof zone === "string"
                          ? zone
                          : zone.zone_name ??
                            zone.name ??
                            zone.zone_code ??
                            zoneId;

                      return (
                        <MenuItem key={zoneId} value={zoneId}>
                          {zoneLabel}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </FormControl>

        <Container style={styles.rulesContainer}>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsRightLenght ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene 8 caracteres como mínimo</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsLetter ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene letras</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsNumbers ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene números</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle size="18" style={containsSpecialCharacters ? styles.successRuleIcon : styles.errorRuleIcon} variant="Bold"/>
            <Typography variant="body2">Contiene caracteres especiales</Typography>
          </Container>
        </Container>

        <Container
          style={{
            display: "flex",
            justifyContent: "end",
            width: "100%",
            padding: 0,
            marginTop: 36,
            gap: 12,
          }}
        >
          <Button
            variant="standard"
            color="primary"
            onClick={handleDialogClose}
            style={{
              width: "120px",
              borderRadius: "0.5rem",
              height: "38px",
              color: red["A400"],
              backgroundColor: red["50"],
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid}
            onClick={handleCreateDeliveryClick}
            sx={isFormValid ? activeButtonStyles : inactiveButtonStyles}
          >
            {isLoading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              "Crear"
            )}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
